import { focusManager } from "@tanstack/react-query";
import * as React from "react";
import { Link } from "react-router";

import type {
  OrgLlmConnectionKind,
  OrgLlmConnectionRecord,
} from "@/shared/domain/orgs";
import {
  useCreateOrgLlmConnection,
  useUpdateOrgLlmConnection,
} from "@/shared/domain/orgs/hooks";
import { ORG_LLM_CONNECTION_KIND_LABELS } from "@/shared/domain/orgs/orgModelConfig";
import { useOrgSecrets } from "@/shared/domain/secrets/hooks/useOrgSecrets";
import {
  Dialog,
  Radio,
  RadioGroup,
  RobotoButton,
} from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http/userFacingErrorMessage";
import { Goto } from "@/shared/navigation/Goto";

import { TextField } from "../TextField";

import {
  connectionDraftError,
  connectionRequest,
  connectionSettingsChanged,
  initialConnectionDraft,
  withConnectionKind,
  type ConnectionDraft,
  type ConnectionTemplates,
} from "./connectionDraft";
import styles from "./OrgLlmConnections.module.css";

const KINDS: OrgLlmConnectionKind[] = ["anthropic_api", "openai_compat"];

/** Secret choice for "no key", on an OpenAI-compatible endpoint without authentication. */
const NO_SECRET = "";

interface ConnectionDialogProps {
  orgId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The connection to edit; omitted to add one. */
  connection?: OrgLlmConnectionRecord;
  templates: ConnectionTemplates;
}

/** Add or edit one of the org's own LLM connections. */
export function ConnectionDialog({
  orgId,
  open,
  onOpenChange,
  connection,
  templates,
}: ConnectionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className={styles.dialog}>
        {open && (
          <ConnectionForm
            // Remount per connection so the draft starts from it; closing unmounts the form.
            key={connection?.connection_id ?? "new"}
            orgId={orgId}
            connection={connection}
            templates={templates}
            onDone={() => onOpenChange(false)}
          />
        )}
      </Dialog.Content>
    </Dialog>
  );
}

function ConnectionForm({
  orgId,
  connection,
  templates,
  onDone,
}: {
  orgId: string;
  connection: OrgLlmConnectionRecord | undefined;
  templates: ConnectionTemplates;
  onDone: () => void;
}) {
  const createMutation = useCreateOrgLlmConnection();
  const updateMutation = useUpdateOrgLlmConnection();
  const isNew = connection === undefined;
  const mutation = isNew ? createMutation : updateMutation;
  const secretsQuery = useOrgSecrets(orgId, 100);
  const secretNames = secretsQuery.data?.items.map((s) => s.name) ?? [];
  // The secrets links open Settings › Secrets in a new tab. Refetch whenever
  // this tab regains focus, so a secret added there can be picked here: React
  // Query's own refetch on focus skips data younger than the app's staleTime.
  const refetchSecrets = secretsQuery.refetch;
  React.useEffect(
    () =>
      focusManager.subscribe((focused) => {
        if (focused) {
          void refetchSecrets();
        }
      }),
    [refetchSecrets],
  );
  const [draft, setDraft] = React.useState<ConnectionDraft>(() =>
    initialConnectionDraft(connection, templates),
  );

  // Validation messages wait for the first Save, so an empty form doesn't open on an error.
  const [saveAttempted, setSaveAttempted] = React.useState(false);
  const error = connectionDraftError(draft, isNew ? "add" : "edit");
  const isAnthropic = draft.kind === "anthropic_api";
  const saving = mutation.isPending;
  // The server calls a model before storing a new connection or changed
  // settings; a label-only edit is stored straight away.
  const testsOnSave = isNew || connectionSettingsChanged(draft, connection);

  const handleSave = () => {
    setSaveAttempted(true);
    if (error !== null) {
      return;
    }
    const request = connectionRequest(draft);
    if (isNew) {
      createMutation.mutate({ orgId, request }, { onSuccess: onDone });
    } else {
      updateMutation.mutate(
        { orgId, connectionId: connection.connection_id, request },
        { onSuccess: onDone },
      );
    }
  };

  return (
    <div className={styles.dialogBody}>
      <Dialog.Title>
        {isNew ? "Add LLM connection" : `Edit ${connection.label}`}
      </Dialog.Title>
      <RadioGroup<OrgLlmConnectionKind>
        legend="Kind"
        value={draft.kind}
        onValueChange={(kind) =>
          setDraft(withConnectionKind(draft, kind, templates))
        }
        disabled={saving}
      >
        {KINDS.map((kind) => (
          <Radio<OrgLlmConnectionKind>
            key={kind}
            value={kind}
            label={ORG_LLM_CONNECTION_KIND_LABELS[kind]}
          />
        ))}
      </RadioGroup>
      <TextField
        label="Label"
        placeholder="e.g. Acme Anthropic key"
        value={draft.label}
        onChange={(label) => setDraft({ ...draft, label })}
        disabled={saving}
      />
      <TextField
        label={isAnthropic ? "Gateway URL (optional)" : "Base URL"}
        description={
          isAnthropic
            ? "Leave empty to call the Anthropic API directly."
            : "The endpoint's OpenAI-compatible root, e.g. https://llm.example.com/v1."
        }
        value={draft.base_url}
        onChange={(base_url) => setDraft({ ...draft, base_url })}
        disabled={saving}
      />
      {/* A radio list rather than a Select: the Select popup layers below
          the Dialog. */}
      <RadioGroup<string>
        legend={isAnthropic ? "API key secret" : "API key secret (optional)"}
        description={
          <>
            The org secret holding the provider&apos;s API key.{" "}
            <SecretsLink>Manage secrets</SecretsLink>
          </>
        }
        value={draft.api_key_secret_name}
        onValueChange={(api_key_secret_name) =>
          setDraft({ ...draft, api_key_secret_name })
        }
        disabled={saving}
      >
        {!isAnthropic && (
          <Radio<string> value={NO_SECRET} label="None (no authentication)" />
        )}
        {secretNames.map((name) => (
          <Radio<string> key={name} value={name} label={name} />
        ))}
      </RadioGroup>
      {secretsQuery.isSuccess && secretNames.length === 0 && (
        <p className={styles.note}>
          This organization has no secrets yet.{" "}
          <SecretsLink>Add the API key as a secret</SecretsLink>, then pick it
          here.
        </p>
      )}
      <TextField
        label="Test with model"
        placeholder={isAnthropic ? "e.g. claude-haiku-4-5" : "e.g. llama-4"}
        description={
          isNew
            ? "The provider's id of a model on this connection. Saving calls it once and keeps the connection only if the call succeeds."
            : "Used when you change the kind, URL or secret. Leave empty to test with a switched-on, working model on your list that runs on this connection."
        }
        value={draft.test_model_id}
        onChange={(test_model_id) => setDraft({ ...draft, test_model_id })}
        disabled={saving}
      />

      {saveAttempted && error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      {saving && testsOnSave && (
        <p className={styles.note} role="status">
          Testing the connection…
        </p>
      )}
      {mutation.isError && (
        <p className={styles.error} role="alert">
          {userFacingErrorMessage(mutation.error)}
        </p>
      )}
      <div className={styles.actions}>
        <RobotoButton variant="secondary" onClick={onDone} disabled={saving}>
          Cancel
        </RobotoButton>
        <RobotoButton onClick={handleSave} loading={saving}>
          Save
        </RobotoButton>
      </div>
    </div>
  );
}

/** A link to Settings › Secrets in a new tab, so the half-filled dialog survives. */
function SecretsLink({ children }: { children: React.ReactNode }) {
  return (
    <Link
      to={Goto.route.Settings.Secrets}
      target="_blank"
      rel="noopener"
      className={styles.hintLink}
    >
      {children}
    </Link>
  );
}
